import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Language, translations, Translations } from './translations';

type LanguageContextValue = {
  language: Language;
  direction: 'ltr' | 'rtl';
  toggleLanguage: () => void;
  setLanguage: (lang: Language) => void;
  t: <T = string>(path: string) => T;
};

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const STORAGE_KEY = 'portfolio-language';

const isArabicPath = (pathname: string) => pathname === '/ar' || pathname.startsWith('/ar/');

const getLanguageFromPath = (): Language | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return isArabicPath(window.location.pathname.toLowerCase()) ? 'ar' : 'en';
};

const getPathForLanguage = (lang: Language) => (lang === 'ar' ? '/ar/' : '/');

const getFromPath = (path: string, langPack: Translations) => {
  const segments = path.split('.');
  return segments.reduce<any>((acc, key) => (acc ? acc[key as keyof typeof acc] : undefined), langPack);
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const pathLanguage = getLanguageFromPath();
    if (pathLanguage) {
      return pathLanguage;
    }

    if (typeof window === 'undefined') {
      return 'en';
    }

    const stored = window.localStorage.getItem(STORAGE_KEY) as Language | null;
    return stored === 'ar' || stored === 'en' ? stored : 'en';
  });

  const syncPathWithLanguage = useCallback((lang: Language, pushState: boolean) => {
    if (typeof window === 'undefined') {
      return;
    }

    const targetPath = getPathForLanguage(lang);
    const currentPath = window.location.pathname;
    const isCurrentArabic = isArabicPath(currentPath.toLowerCase());

    if ((lang === 'ar' && !isCurrentArabic) || (lang === 'en' && isCurrentArabic)) {
      const nextUrl = `${targetPath}${window.location.search}${window.location.hash}`;
      if (pushState) {
        window.history.pushState({}, '', nextUrl);
      } else {
        window.history.replaceState({}, '', nextUrl);
      }
    }
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    syncPathWithLanguage(lang, true);
  }, [syncPathWithLanguage]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, language);
    }
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handlePopState = () => {
      const pathLanguage = getLanguageFromPath();
      if (pathLanguage && pathLanguage !== language) {
        setLanguageState(pathLanguage);
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [language]);

  const direction: 'ltr' | 'rtl' = language === 'ar' ? 'rtl' : 'ltr';

  const toggleLanguage = useCallback(() => {
    const nextLanguage: Language = language === 'en' ? 'ar' : 'en';
    setLanguage(nextLanguage);
  }, [language, setLanguage]);

  const t = useCallback(<T,>(path: string) => {
    const langPack = translations[language];
    const value = getFromPath(path, langPack) as T;
    return (value as unknown as T) ?? (path as unknown as T);
  }, [language]);

  const value = useMemo(
    () => ({ language, direction, toggleLanguage, setLanguage, t }),
    [language, direction, toggleLanguage, setLanguage, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return ctx;
};
